import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import {
  api,
  useGetOpsAppObjectDefinitionsQuery,
  useListOpsAppObjectsQuery,
  useLazyListOpsAppObjectsQuery,
  useGetOpsAppObjectQuery,
  useLazyGetOpsAppObjectQuery
} from "@webcore/services/api/api";
import {
  OpsAppObject,
  OpsAppObjectTypeDefinition,
  OpsAppObjectValue
} from "@webcore/services/api/models";

/**
 * The flat object shape handed to custom view code: the declared (snake_case) fields at
 * the top level plus the platform-managed `id` and `sequenceNumber`. Declared field names can
 * never collide with the camelCase platform keys.
 */
export interface OpsAppObjectRecord {
  id: string;
  sequenceNumber: number;
  [field: string]: OpsAppObjectValue;
}

/**
 * The result of a live read hook — the RTK Query shape, narrowed to the fields custom view code
 * needs, with `data` flattened to `OpsAppObjectRecord`(s). The hook re-renders and hands back a new
 * result whenever the object type changes (this session's own writes and remote SignalR-driven cache
 * invalidations).
 */
export interface OpsAppObjectsQueryResult<TData> {
  /** The current data, or undefined before the first successful load. */
  data: TData | undefined;
  /** True only while loading for the first time (no cached data yet). */
  isLoading: boolean;
  /** True whenever a request is in flight, including background refetches after an invalidation. */
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  /** The error, when `isError`. */
  error: unknown;
  /** Force an immediate refetch. */
  refetch: () => void;
}

/** Fetches a page on demand; resolves the flattened records. */
export type OpsAppObjectsListTrigger = (tableName: string, skip: number, take: number) => Promise<OpsAppObjectRecord[]>;

/** Fetches a single object on demand; resolves the flattened record, or null when it does not exist. */
export type OpsAppObjectGetTrigger = (tableName: string, id: string) => Promise<OpsAppObjectRecord | null>;

/**
 * The `opsAppObjects` member of the custom view host scope. Mirrors the ambient declarations that
 * custom view projects consume from their `src/globals.d.ts`. Only custom views receive this API —
 * widgets are legacy and get only their bound KPI's data.
 *
 * Reads follow the RTK Query pattern. Each read has a **live** hook (auto-updates on change) and a
 * **lazy** hook (fetch on demand via the returned trigger, e.g. from an event handler). Call the
 * hooks at the top level of your view, following the rules of hooks.
 */
export interface OpsAppObjectsHostApi {
  /** The object types declared in the pipeline config (data-pipelines/main/objects/). */
  readonly definitions: ReadonlyArray<OpsAppObjectTypeDefinition>;
  /** Server-resolved: whether the current session can write objects (Editor+ with write scope). */
  readonly canWrite: boolean;

  /**
   * Live query for a page of objects — the `take`-sized slice starting at `skip`, ordered by
   * sequenceNumber. Auto-updates when the object type changes. There is no "get everything" call:
   * an object type can hold a large data set, so page yourself by advancing `skip` (a page shorter
   * than `take` is the last one).
   */
  useListOpsAppObjects(tableName: string, skip: number, take: number): OpsAppObjectsQueryResult<OpsAppObjectRecord[]>;
  /**
   * Live query for a single object by id. Auto-updates when the object changes. A missing object
   * resolves to `isError` (a 404); check `isError` rather than expecting `data === undefined`.
   */
  useGetOpsAppObject(tableName: string, id: string): OpsAppObjectsQueryResult<OpsAppObjectRecord>;

  /**
   * Lazy version of {@link useListOpsAppObjects}: returns `[fetchPage, result]`. Call `fetchPage`
   * on demand; `result` then auto-updates like the live query.
   */
  useLazyListOpsAppObjects(): [OpsAppObjectsListTrigger, OpsAppObjectsQueryResult<OpsAppObjectRecord[]>];
  /**
   * Lazy version of {@link useGetOpsAppObject}: returns `[fetchObject, result]`. `fetchObject`
   * resolves null when the object does not exist.
   */
  useLazyGetOpsAppObject(): [OpsAppObjectGetTrigger, OpsAppObjectsQueryResult<OpsAppObjectRecord>];

  /**
   * Inserts or replaces an object. Mints an id when the object carries none. Replacements of
   * objects already in cached reads update optimistically — live query hooks reflect the write
   * immediately, before the server confirms (and roll back if the write fails). Newly inserted
   * objects appear once the server confirms and the refetch lands, since their sequence position
   * isn't known client-side.
   */
  upsert(tableName: string, object: { id?: string } & Record<string, OpsAppObjectValue>): Promise<OpsAppObjectRecord>;
  /**
   * Deletes an object; resolves whether it existed. Cached reads update optimistically — live
   * query hooks drop the object immediately, before the server confirms (and roll back if the
   * delete fails).
   */
  remove(tableName: string, id: string): Promise<boolean>;
}

type ApiDispatch = ThunkDispatch<Record<string, unknown>, unknown, UnknownAction>;

function flattenObject(item: OpsAppObject): OpsAppObjectRecord {
  return {
    ...item.properties,
    id: item.id,
    sequenceNumber: item.sequenceNumber
  };
}

function isNotFoundError(error: unknown): boolean {
  return typeof error === "object" && error !== null && "status" in error
    && (error as { status: unknown }).status === 404;
}

// The host read hooks: thin wrappers over the shared RTK Query hooks that flatten each stored
// object to an OpsAppObjectRecord. Each is produced by a factory that closes over the renderer's
// dataPipelineConfigurationId, so the id is transparent to custom view code (mirroring
// fetchKpiData). The returned functions are plain custom hooks — the view calls them inside its
// own render, so they resolve the host app's store context just like any RTK Query hook.

function createUseListObjects(dataPipelineConfigurationId: string) {
  return function useListObjects(tableName: string, skip: number, take: number): OpsAppObjectsQueryResult<OpsAppObjectRecord[]> {
    const result = useListOpsAppObjectsQuery({ dataPipelineConfigurationId, tableName, skip, take });
    const data = useMemo(() => result.data?.items.map(flattenObject), [result.data]);
    return { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch: result.refetch };
  };
}

function createUseGetObject(dataPipelineConfigurationId: string) {
  return function useGetObject(tableName: string, id: string): OpsAppObjectsQueryResult<OpsAppObjectRecord> {
    const result = useGetOpsAppObjectQuery({ dataPipelineConfigurationId, tableName, objectId: id });
    const data = useMemo(() => (result.data ? flattenObject(result.data) : undefined), [result.data]);
    return { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch: result.refetch };
  };
}

function createUseLazyListObjects(dataPipelineConfigurationId: string) {
  return function useLazyListObjects(): [OpsAppObjectsListTrigger, OpsAppObjectsQueryResult<OpsAppObjectRecord[]>] {
    // Lazy query state carries no refetch (lazy queries re-run through the trigger); refetch re-issues
    // the last-triggered args, forcing a network round-trip (preferCacheValue: false).
    const [trigger, result, { lastArg }] = useLazyListOpsAppObjectsQuery();
    const data = useMemo(() => result.data?.items.map(flattenObject), [result.data]);
    const fetchPage = useCallback<OpsAppObjectsListTrigger>(
      async (tableName, skip, take) => {
        const response = await trigger({ dataPipelineConfigurationId, tableName, skip, take }).unwrap();
        return response.items.map(flattenObject);
      },
      [trigger]
    );
    const refetch = useCallback(() => {
      if (lastArg !== undefined) {
        void trigger(lastArg, false);
      }
    }, [trigger, lastArg]);
    return [fetchPage, { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch }];
  };
}

function createUseLazyGetObject(dataPipelineConfigurationId: string) {
  return function useLazyGetObject(): [OpsAppObjectGetTrigger, OpsAppObjectsQueryResult<OpsAppObjectRecord>] {
    const [trigger, result, { lastArg }] = useLazyGetOpsAppObjectQuery();
    const data = useMemo(() => (result.data ? flattenObject(result.data) : undefined), [result.data]);
    const fetchObject = useCallback<OpsAppObjectGetTrigger>(
      async (tableName, id) => {
        try {
          const item = await trigger({ dataPipelineConfigurationId, tableName, objectId: id }).unwrap();
          return flattenObject(item);
        } catch (error) {
          if (isNotFoundError(error)) {
            return null;
          }
          throw error;
        }
      },
      [trigger]
    );
    const refetch = useCallback(() => {
      if (lastArg !== undefined) {
        void trigger(lastArg, false);
      }
    }, [trigger, lastArg]);
    return [fetchObject, { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch }];
  };
}

/**
 * Builds the `opsAppObjects` host API injected into custom view code. Returns null until the
 * definitions have loaded so renderers can gate execution on a fully-initialized scope.
 *
 * Reads are the shared RTK Query hooks (flattened to `OpsAppObjectRecord`); writes are imperative
 * mutations that optimistically patch the cached read results (rolled back on failure), so live
 * hooks reflect a write immediately. The `OpsAppObjects` cache tag then reconciles everything
 * authoritatively: upserts/removes invalidate it, SignalR change notifications (direct or
 * MFE-forwarded) invalidate it, and every live read hook refetches on invalidation and re-renders
 * its caller.
 */
export function useOpsAppObjectsHostApi(dataPipelineConfigurationId: string): OpsAppObjectsHostApi | null {
  const dispatch = useDispatch<ApiDispatch>();
  const { data: definitionsResponse } = useGetOpsAppObjectDefinitionsQuery({ dataPipelineConfigurationId });

  return useMemo(() => {
    if (definitionsResponse === undefined) {
      return null;
    }

    return {
      definitions: definitionsResponse.objectTypes,
      canWrite: definitionsResponse.canWrite,

      useListOpsAppObjects: createUseListObjects(dataPipelineConfigurationId),
      useGetOpsAppObject: createUseGetObject(dataPipelineConfigurationId),
      useLazyListOpsAppObjects: createUseLazyListObjects(dataPipelineConfigurationId),
      useLazyGetOpsAppObject: createUseLazyGetObject(dataPipelineConfigurationId),

      upsert: async (tableName: string, object: { id?: string } & Record<string, OpsAppObjectValue>): Promise<OpsAppObjectRecord> => {
        const { id, ...properties } = object;
        const objectId = typeof id === "string" && id.length > 0 ? id : crypto.randomUUID();
        const item = await dispatch(api.endpoints.upsertOpsAppObject.initiate(
          { dataPipelineConfigurationId, tableName, objectId, properties }
        )).unwrap();
        return flattenObject(item);
      },

      remove: async (tableName: string, id: string): Promise<boolean> => {
        const response = await dispatch(api.endpoints.deleteOpsAppObject.initiate(
          { dataPipelineConfigurationId, tableName, objectId: id }
        )).unwrap();
        return response.deleted;
      }
    };
  }, [definitionsResponse, dispatch, dataPipelineConfigurationId]);
}
